import PropTypes from "prop-types"
import * as css from "./SideBySide.module.css"

/**
 * SideBySide - Lay an image beside a block of body content in two columns.
 *
 * Unlike a floated image, the text sits in its own flex column and never
 * wraps back underneath the image, so there are no orphaned trailing lines.
 * Columns stack vertically on mobile.
 *
 * Children are normal markdown (paragraphs, headings, lists), so the body
 * keeps full article styling.
 *
 * Usage in markdown:
 *
 *   <sidebyside image="/images/blog/my-post/portrait.jpg" alt="A portrait">
 *
 *   The body copy goes here, and can span
 *
 *   ### Multiple blocks
 *
 *   including headings and lists.
 *
 *   </sidebyside>
 *
 * Props:
 *   image  - image URL (absolute, e.g. under /static)
 *   alt    - alt text for the image
 *   align  - "left" (default) or "right" for which side the image sits on
 *   width  - image column width (default 240px), e.g. "200px" or "30%"
 */
function SideBySide({ image, alt, align = "left", width, children }) {
  if (!image) return <>{children}</>

  return (
    <div
      className={`${css.root} ${align === "right" ? css.right : ""}`}
      style={width ? { "--sbs-media-width": width } : undefined}
    >
      <div className={css.media}>
        <img className={css.img} src={image} alt={alt || ""} loading="lazy" />
      </div>
      <div className={css.body}>{children}</div>
    </div>
  )
}

SideBySide.propTypes = {
  /** Image URL (absolute path, typically under /static) */
  image: PropTypes.string,
  /** Alt text for the image */
  alt: PropTypes.string,
  /** Which side the image sits on */
  align: PropTypes.oneOf(["left", "right"]),
  /** Image column width (e.g. "240px" or "30%") */
  width: PropTypes.string,
  /** Body content rendered in the text column */
  children: PropTypes.node,
}

export default SideBySide
